import { useState } from 'react';
import { Modal, TextInput, Textarea, Stack, Group, Button, Alert } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { api } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';

export interface Contact {
  id: number;
  customerId: number;
  name: string;
  phone: string | null;
  email: string | null;
  note: string | null;
}

export default function ContactModal({
  customerId,
  initial,
  onClose,
  onDone,
  onLocalSave,
  onLocalDelete,
}: {
  customerId?: number;
  initial: Contact | null;
  onClose: () => void;
  onDone: () => void;
  onLocalSave?: (contact: Omit<Contact, 'id' | 'customerId'>) => void;
  onLocalDelete?: () => void;
}) {
  const localMode = !!onLocalSave;
  const { me } = useAuth();
  const writable = canWrite(me?.role);

  const [name, setName] = useState(initial?.name ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setErr(null);
    if (!name.trim()) return setErr('Name is required.');
    if ((note ?? '').length > 500) return setErr('Note must be ≤ 500 chars.');
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setErr('Invalid e-mail.');
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        note: note.trim() || null,
      };
      if (localMode) {
        onLocalSave!(body);
        return;
      }
      if (initial) {
        await api.put(`/customers/${customerId}/contacts/${initial.id}`, body);
      } else {
        await api.post(`/customers/${customerId}/contacts`, body);
      }
      notifications.show({ message: 'Contact saved.', color: 'green' });
      onDone();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete() {
    if (!initial) return;
    modals.openConfirmModal({
      title: `Delete ${initial.name}?`,
      children: 'This contact will be removed from the customer.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        if (onLocalDelete) { onLocalDelete(); return; }
        try {
          await api.delete(`/customers/${customerId}/contacts/${initial.id}`);
          notifications.show({ message: 'Contact deleted.', color: 'gray' });
          onDone();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Delete failed', color: 'red' });
        }
      },
    });
  }

  return (
    <Modal
      opened
      onClose={onClose}
      size="md"
      title={<span className="modal-title-custom">{initial ? 'Edit Contact' : 'Add Contact'}</span>}
    >
      <Stack>
        <TextInput
          data-tour="contact-name"
          label="Contact Name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          maxLength={120}
          disabled={!writable}
        />
        <TextInput
          label="Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
          maxLength={60}
          disabled={!writable}
        />
        <TextInput
          data-tour="contact-email"
          label="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          maxLength={160}
          disabled={!writable}
        />
        <Textarea
          label="Note"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          maxLength={500}
          autosize
          minRows={3}
          description={`${note.length}/500`}
          disabled={!writable}
        />
        {err && <Alert color="red">{err}</Alert>}
        <Group justify="space-between" mt="md">
          <div>
            {initial && writable && (
              <Button color="red" variant="light" onClick={confirmDelete}>Delete</Button>
            )}
          </div>
          <Group>
            <Button variant="default" onClick={onClose}>Close</Button>
            {writable && <Button onClick={save} loading={busy} data-tour="contact-save">Save</Button>}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
